import React, {useState, useEffect, useRef} from 'react';
import {View, Text, Pressable} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {ObjectId, UUID} from 'bson';
import Icon from '../Shared/Icon';
import CustomPressable from '../Shared/CustomPressable';
import ConfirmModal from '../Shared/ConfirmModal';
import {DynamicScreenButton} from '../../Types/ControlTypes';
import {usePermission} from '../../Providers/PermissionProvider';
import {SubmissionStatus} from '../../Models/PowerSyncModels/models';
import {Submission} from '../../Models/PowerSyncModels/models';
import {SubmissionTraining} from '../../Models/PowerSyncModels/models';
import {Navigation} from '../../Constants/Navigation';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {
  TemplateCategoryTypes,
  TemplateTypes,
} from '../../Constants/TemplateCategoryTypes';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import Colors from '../../Styles/Shared/Colors';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import GeneralHelperService from '../../Services/GeneralHelperService';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';

type ScreenButtonsProps = {
  navigation: any;
  category: string;
  permissions?: string;
  showExtraButtons?: boolean;
};

const ScreenButtons = (props: ScreenButtonsProps): React.ReactElement => {
  const [screenButtons, setScreenButtons] = useState<DynamicScreenButton[]>([]);
  const [canCreate, setCanCreate] = useState<boolean>();

  const {hasPermission} = usePermission();

  const {
    templateCategories,
    templates,
    templateVersions,
    submissionStatuses: db_submissionStatuses,
    powersyncReady,
  } = usePowerSyncData();

  const {
    updateSubmission,
    updateSubmissionTraining,
    createSubmission,
    createSubmissionTraining,
  } = usePowerSyncWriteData();

  const user = usePowerSyncUser();

  const createModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });
  const helperService: IGeneralHelperService = new GeneralHelperService();
  
  useEffect(() => {
    let createReport = props.permissions
      ? hasPermission(props.permissions)
      : true;
    setCanCreate(createReport);

    const fetchScreenButtons = async () => {
      let templateCategory = templateCategories
        .find(t => t.name === props.category);
        
      let actionTemplateIds = templateCategory?.actionTemplateIds ?? [];

      let filteredTemplates = templates
        .filter(t => actionTemplateIds.includes(t._id))
        .sort((a, b) => a.row - b.row);

      let newScreenButtons: DynamicScreenButton[] = [];
      for (let i = 0; i < filteredTemplates.length; i++) {
        let templateId: string = filteredTemplates[i]._id;
        let versions = templateVersions
          .filter(x => x.templateId === templateId)
          .sort((a, b) => b.version - a.version);

        if (versions && versions.length > 0) {
          newScreenButtons.push({
            templateName: filteredTemplates[i].name,
            label: versions[0].displayName ?? filteredTemplates[i].name,
            icon: filteredTemplates[i].iconSvg ? helperService.getTemplateIcon(filteredTemplates[i].iconSvg, Colors.green, Colors.white) : '',
            version: versions[0].version,
          });
        }
      }

      setScreenButtons(newScreenButtons);
    };

    fetchScreenButtons();
  }, [powersyncReady]);

  async function openDynamicForm(
    templateName: string,
    showConfirmation?: boolean,
  ): Promise<void> {
    if (!canCreate) {
      createModalCommand.current.openModal();
      return;
    }

    let template = templates.find(t => t.name === templateName);

    if (template) {
      let templateId = template._id;
      let versions = templateVersions
        .filter(x => x.templateId === templateId)
        .sort((a, b) => b.version - a.version)
        .map(x => ({_id: x._id, name: x.name}));

      let newDraftId = new ObjectId().toString();
      const draftStatus: SubmissionStatus = db_submissionStatuses.find(
        x => x.name == SubmissionStatuses.DRAFT,
      )!;

      let userInfo = user.getUserInfo();
      let org = userInfo.orgId.toString();
      let currentEmail = userInfo.email;

      let newSubmission: Submission = {
        _id: newDraftId,
        partition: org,
        createdBy: currentEmail as string,
        updatedBy: currentEmail as string,
        templateVersion: {
          _id: versions[0]._id,
          name: versions[0].name,
        },
        templateType: {
          _id: template!.templateType._id,
          name: template!.templateType.name,
        },
        submissionStatus: {
          _id: draftStatus._id,
          name: draftStatus.name,
        },
        SQLServerId: new UUID().toString().toUpperCase(),
        answers: [],
        metadataJSON: '',
        attachmentsUploaded: true,
        createDateTimeStamp: new Date(),
        updateDateTimeStamp: new Date(),
      };

      if (template.templateType.name === TemplateTypes.TRAINING_BREAKDOWN) {
        let newSubmissionTraining: SubmissionTraining = {
          ...newSubmission,
          person: null,
          trainingType: null,
          certificateType: null,
        };

        await createSubmissionTraining(newSubmissionTraining);

        if (newDraftId == null) throw Error('Error: create draft failed!');

        props.navigation.navigate(Navigation.TRAININGFORM, {
          submissionId: newDraftId,
        });
      } else {
        await createSubmission(newSubmission);

        if (newDraftId == null) throw Error('Error: create draft failed!');

        props.navigation.navigate(Navigation.DYNAMICFORM, {
          submissionId: newDraftId,
          showConfirmation: showConfirmation,
          submissionStatus: SubmissionStatuses.DRAFT,
        });
      }
    }
  }

  const goToAddPerson = (personId: ObjectId | null) => {
    props.navigation.push(Navigation.EDITPERSON, {
      personId: personId,
    });
  };

  const goToTrainingBreakdown = () => {
    props.navigation.push(Navigation.TRAININGBREAKDOWN);
  };

  const goToEquipmentLog = () => {
    props.navigation.push(Navigation.EQUIPMENTLOG, {});
  };

  const renderScreenButton = (
    button: DynamicScreenButton,
    index: number,
  ): React.ReactElement => {
    return (
      <React.Fragment key={index}>
        <CustomPressable
          style={({pressed}) => [
            CommonStyleSheet.screenButton,
            {overflow: 'visible'},
            !canCreate && {opacity: 0.4},
            pressed && {opacity: 0.6},
          ]}
          onPress={() => openDynamicForm(button.templateName, false)}>
          {button.icon !== '' && (
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(button.icon)}`}
            />
          )}
          <Text style={CommonStyleSheet.screenButtonText}>{button.label}</Text>
        </CustomPressable>
        {index % 2 === 1 && <View style={{flexBasis: '100%'}}></View>}
      </React.Fragment>
    );
  };

  return (
    <>
      <View style={CommonStyleSheet.screenButtonsContainer}>
        {screenButtons.map((screenButton, index) => {
          return renderScreenButton(screenButton, index);
        })}
        {props.category == TemplateCategoryTypes.PEOPLE &&
          screenButtons.length > 0 && (
            <React.Fragment key={screenButtons.length}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.screenButton,
                  pressed && {opacity: 0.6},
                ]}
                onPress={() => goToTrainingBreakdown()}>
                <Icon
                  icon={'training-breakdown'}
                  color={Colors.green}
                  size={24}
                />
                <Text style={CommonStyleSheet.screenButtonText}>
                  Training Breakdown
                </Text>
              </Pressable>
              {screenButtons.length % 2 === 1 && (
                <View style={{flexBasis: '100%'}}></View>
              )}
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.screenButton,
                  pressed && {opacity: 0.6},
                ]}
                onPress={() => goToAddPerson(null)}>
                <Icon icon={'user-plus'} color={Colors.green} size={24} />
                <Text style={CommonStyleSheet.screenButtonText}>
                  Add a person
                </Text>
              </Pressable>
            </React.Fragment>
          )}
        {props.category == TemplateCategoryTypes.EQUIPMENT &&
          props.showExtraButtons != false &&
          screenButtons.length > 0 && (
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.screenButton,
                pressed && {opacity: 0.6},
              ]}
              onPress={() => goToEquipmentLog()}>
              <Icon icon={'clipboard'} color={Colors.green} size={24} />
              <Text style={CommonStyleSheet.screenButtonText}>
                Equipment Log
              </Text>
            </Pressable>
          )}
      </View>
      <ConfirmModal
        title='No Access'
        message='You do not have access to create reports'
        okClick={() => {}}
        okText='Ok'
        setOpenModal={(func: () => void) => {
          createModalCommand.current.setOpenModal(func);
        }}
      />
    </>
  );
};

export default ScreenButtons;
